"use strict";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result) __defProp(target, key, result);
  return result;
};
import { LitElement, html } from "lit";
import { property, state } from "lit/decorators.js";
import { STCComponentMixin } from "../../common/mixins/stc_component_mixin";
import { SmsLengthPreview } from "../../sms_length_preview.js";
const COUNTER_STYLES = `
#counter {
  display: block !important;
  margin: 0;
  margin-top: var(--hi-px-4);
  text-align: right;
  color: var(--hi-clr--gray-8);
}

#counter.is-over-limit {
  color: var(--hi-clr--red-5);
}
`;
function parseBooleanAttribute(value) {
  if (value === null) {
    return false;
  }
  return value !== "false" && value !== "0";
}
const DEFAULT_FIELD_ID = "sms_body";
const DEFAULT_MESSAGE_PARAM_NAME = "sms_message[message]";
const DEFAULT_SHORTEN_LINKS_PARAM_NAME = "sms_message[shorten_links]";
const DEFAULT_SUBMIT_BUTTON_ID = "sms_submit";
const _LinkShorteningSMSField = class _LinkShorteningSMSField extends STCComponentMixin(LitElement) {
  constructor() {
    super(...arguments);
    this.shortUrlLength = 0;
    this.messageLimit = 300;
    this.messageLengthTooltip = "";
    this.fieldId = DEFAULT_FIELD_ID;
    this.messageParamName = DEFAULT_MESSAGE_PARAM_NAME;
    this.shortenLinksParamName = DEFAULT_SHORTEN_LINKS_PARAM_NAME;
    this.submitButtonId = DEFAULT_SUBMIT_BUTTON_ID;
    this.disabled = false;
    this.includeShortenLinksParam = true;
    this.initialMessage = "";
    this.messageValue = "";
    this.textareaEl = null;
    this.submitButton = null;
    this.submitButtonOriginallyDisabled = false;
    this.submitButtonOriginallyLocked = false;
    this.submitButtonStateCaptured = false;
    this.textareaCoreInitialized = false;
    this.counterReady = false;
    this.counterStylesInjected = false;
    this.textareaInitAttempts = 0;
    this.formEl = null;
    this.handleFormSubmit = () => {
      this.syncMessageHiddenInput();
      this.syncShortenLinksHiddenInput();
    };
    this.handleMessageChange = () => {
      this.messageValue = this.readMessage();
      this.refreshCounterAndValidity();
    };
  }
  connectedCallback() {
    super.connectedCallback();
    this.formEl = this.closest("form");
    this.formEl?.addEventListener("submit", this.handleFormSubmit);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.formEl?.removeEventListener("submit", this.handleFormSubmit);
    this.formEl = null;
    if (this.textareaEl) {
      this.textareaEl.removeEventListener("value-changed", this.handleMessageChange);
    }
    if (this.submitButton && this.submitButtonStateCaptured) {
      this.submitButton.disabled = this.submitButtonOriginallyDisabled;
      this.submitButton.locked = this.submitButtonOriginallyLocked;
    }
  }
  updated() {
    this.ensureInitialized();
  }
  isValid() {
    const message = this.readMessage();
    const projected = this.projectedMessageLength(message);
    return message.trim().length > 0 && projected <= this.messageLimit;
  }
  validateForSubmit() {
    this.ensureInitialized();
    this.syncMessageHiddenInput();
    this.syncShortenLinksHiddenInput();
    this.refreshCounterAndValidity({ updateSubmitButton: false });
    return this.isValid();
  }
  usesSubmitButton() {
    return this.submitButtonId !== "";
  }
  syncMessageHiddenInput() {
    const message = this.readMessage();
    const hiddenInput = this.ensureFormHiddenInput(
      "data-sms-body-sync",
      this.fieldId,
      this.messageParamName
    );
    if (hiddenInput) {
      hiddenInput.value = message;
      hiddenInput.disabled = this.disabled;
    }
    this.messageValue = message;
  }
  syncShortenLinksHiddenInput() {
    const form = this.closest("form");
    if (!this.includeShortenLinksParam) {
      form?.querySelector(`input[type="hidden"][data-sms-shorten-links-sync="${this.fieldId}"]`)?.remove();
      return;
    }
    const hiddenInput = this.ensureFormHiddenInput(
      "data-sms-shorten-links-sync",
      this.fieldId,
      this.shortenLinksParamName
    );
    if (hiddenInput) {
      hiddenInput.value = "true";
      hiddenInput.disabled = this.disabled;
    }
  }
  ensureFormHiddenInput(markerAttribute, markerValue, paramName) {
    const form = this.closest("form");
    if (!form) {
      return null;
    }
    const selector = `input[type="hidden"][${markerAttribute}="${markerValue}"]`;
    let hiddenInput = form.querySelector(selector);
    if (!hiddenInput) {
      hiddenInput = document.createElement("input");
      hiddenInput.type = "hidden";
      hiddenInput.setAttribute(markerAttribute, markerValue);
      form.appendChild(hiddenInput);
    }
    hiddenInput.name = paramName;
    return hiddenInput;
  }
  messageLengthTooltipId() {
    return `${this.fieldId}_length_tooltip`;
  }
  ensureInitialized() {
    this.initTextareaCoreOnce();
    if (!this.counterReady) {
      this.initTextareaCounterOnce();
    }
  }
  readMessage() {
    const textarea = this.textareaEl ?? this.querySelector("hi-textarea");
    if (!textarea) {
      return "";
    }
    const internal = textarea.shadowRoot?.querySelector("#input");
    if (internal?.value != null) {
      return String(internal.value);
    }
    return textarea.value?.toString() ?? "";
  }
  projectedMessageLength(message) {
    if (this.shortUrlLength <= 0) {
      return message.length;
    }
    return SmsLengthPreview.estimatedShortenedBody(
      message,
      "0".repeat(this.shortUrlLength)
    ).length;
  }
  initTextareaCoreOnce() {
    if (this.textareaCoreInitialized) {
      return;
    }
    const textarea = this.querySelector("hi-textarea");
    if (!textarea) {
      return;
    }
    this.textareaEl = textarea;
    textarea.removeAttribute("maxlength");
    textarea.maxlength = void 0;
    if (this.initialMessage && !textarea.value) {
      textarea.value = this.initialMessage;
    }
    textarea.addEventListener("value-changed", this.handleMessageChange);
    this.textareaCoreInitialized = true;
    this.syncMessageHiddenInput();
    this.syncShortenLinksHiddenInput();
    this.refreshCounterAndValidity();
  }
  initTextareaCounterOnce() {
    if (this.counterReady || !this.textareaEl) {
      return;
    }
    if (!this.textareaEl.shadowRoot) {
      if (this.textareaInitAttempts < _LinkShorteningSMSField.MAX_TEXTAREA_INIT_ATTEMPTS) {
        this.textareaInitAttempts += 1;
        requestAnimationFrame(() => this.initTextareaCounterOnce());
      }
      return;
    }
    this.textareaInitAttempts = 0;
    this.injectCounterStyles();
    this.counterReady = true;
    const projectedCount = this.projectedMessageLength(this.readMessage());
    this.updateCounter(projectedCount, projectedCount > this.messageLimit);
  }
  refreshCounterAndValidity(options) {
    if (!this.textareaEl) {
      return;
    }
    const projectedCount = this.projectedMessageLength(this.readMessage());
    const overLimit = projectedCount > this.messageLimit;
    const limitMessage = `Message may contain at most ${this.messageLimit} characters.`;
    if (overLimit) {
      this.textareaEl.setCustomValidity?.(limitMessage);
      window.setValidationNotices?.(this.fieldId, [limitMessage]);
      this.textareaEl.validate?.(false);
    } else {
      this.textareaEl.resetCustomValidity?.();
      window.setValidationNotices?.(this.fieldId, []);
      this.textareaEl.validate?.(true);
    }
    if (options?.updateSubmitButton ?? true) {
      this.updateSubmitButton(overLimit);
    }
    this.updateCounter(projectedCount, overLimit);
  }
  injectCounterStyles() {
    if (this.counterStylesInjected || !this.textareaEl?.shadowRoot) {
      return;
    }
    const style = document.createElement("style");
    style.textContent = COUNTER_STYLES;
    this.textareaEl.shadowRoot.appendChild(style);
    this.counterStylesInjected = true;
  }
  updateCounter(projectedCount, overLimit) {
    const counter = this.textareaEl?.shadowRoot?.querySelector("#counter");
    if (!counter) {
      return;
    }
    counter.textContent = `${projectedCount} / ${this.messageLimit}`;
    counter.classList.toggle("is-over-limit", overLimit);
  }
  resolveSubmitButton() {
    if (!this.usesSubmitButton()) {
      return null;
    }
    if (this.submitButton) {
      return this.submitButton;
    }
    this.submitButton = document.getElementById(this.submitButtonId);
    return this.submitButton;
  }
  captureSubmitButtonStateIfNeeded(submitButton) {
    if (this.submitButtonStateCaptured) {
      return;
    }
    this.submitButtonOriginallyDisabled = !!submitButton.disabled;
    this.submitButtonOriginallyLocked = !!submitButton.locked;
    this.submitButtonStateCaptured = true;
  }
  updateSubmitButton(overLimit) {
    const submitButton = this.resolveSubmitButton();
    if (!submitButton) {
      return;
    }
    if (overLimit) {
      this.captureSubmitButtonStateIfNeeded(submitButton);
      submitButton.disabled = true;
      submitButton.locked = false;
      return;
    }
    if (!this.submitButtonStateCaptured) {
      return;
    }
    submitButton.disabled = this.submitButtonOriginallyDisabled;
    submitButton.locked = this.submitButtonOriginallyLocked;
  }
  render() {
    return html`
      <hi-input-group class="mt-5">
        <hi-label for=${this.fieldId} required>
          Message
          ${this.messageLengthTooltip ? html`
                <hi-icon
                  id=${this.messageLengthTooltipId()}
                  kind="info_circle"
                  mode="content"
                  slot="info"
                  label="More info about SMS length"
                ></hi-icon>
                <hi-tooltip for=${this.messageLengthTooltipId()}>${this.messageLengthTooltip}</hi-tooltip>
              ` : null}
        </hi-label>
        <hi-textarea
          id=${this.fieldId}
          html5
          rows="4"
          minlength="1"
          required
          style="width: 100%; display: block;"
          .value=${this.initialMessage}
          ?disabled=${this.disabled}
        ></hi-textarea>
        <hi-validation-notice for=${this.fieldId}></hi-validation-notice>
      </hi-input-group>
    `;
  }
};
_LinkShorteningSMSField.MAX_TEXTAREA_INIT_ATTEMPTS = 30;
__decorateClass([
  property({ type: Number, attribute: "short-url-length" })
], _LinkShorteningSMSField.prototype, "shortUrlLength", 2);
__decorateClass([
  property({ type: Number, attribute: "message-limit" })
], _LinkShorteningSMSField.prototype, "messageLimit", 2);
__decorateClass([
  property({ type: String, attribute: "message-length-tooltip" })
], _LinkShorteningSMSField.prototype, "messageLengthTooltip", 2);
__decorateClass([
  property({ type: String, attribute: "field-id" })
], _LinkShorteningSMSField.prototype, "fieldId", 2);
__decorateClass([
  property({ type: String, attribute: "message-param-name" })
], _LinkShorteningSMSField.prototype, "messageParamName", 2);
__decorateClass([
  property({ type: String, attribute: "shorten-links-param-name" })
], _LinkShorteningSMSField.prototype, "shortenLinksParamName", 2);
__decorateClass([
  property({ type: String, attribute: "submit-button-id" })
], _LinkShorteningSMSField.prototype, "submitButtonId", 2);
__decorateClass([
  property({
    type: Boolean,
    reflect: true,
    converter: {
      fromAttribute: parseBooleanAttribute,
      toAttribute: (value) => value ? "true" : null
    }
  })
], _LinkShorteningSMSField.prototype, "disabled", 2);
__decorateClass([
  property({
    type: Boolean,
    attribute: "include-shorten-links-param",
    converter: {
      fromAttribute: parseBooleanAttribute,
      toAttribute: (value) => value ? "true" : null
    }
  })
], _LinkShorteningSMSField.prototype, "includeShortenLinksParam", 2);
__decorateClass([
  property({ type: String, attribute: "initial-message" })
], _LinkShorteningSMSField.prototype, "initialMessage", 2);
__decorateClass([
  state()
], _LinkShorteningSMSField.prototype, "messageValue", 2);
export let LinkShorteningSMSField = _LinkShorteningSMSField;
customElements.define("link-shortening-sms-field", LinkShorteningSMSField);
